import ReactDOM from 'react-dom/client'
import React from 'react'
import SelectField from './SelectField'
import type { SelectFieldProps, SelectOption, SetterRef } from './Select.types'

declare global {
  interface Window {
    __rdSetters?: Record<string, (value: string, label?: string) => void>
    __rdSelectsPending?: number
    RDRelatedLinks?: {
      updateLinks: (el: { value: string; closest: (sel: string) => Element | null }) => void
    }
  }
}

function copyDataAttrs(from: HTMLElement, to: HTMLElement) {
  for (const [key, value] of Object.entries(from.dataset)) {
    to.dataset[key] = value ?? ''
  }
}

function readBase(el: HTMLElement, sel?: HTMLSelectElement | null) {
  const d = el.dataset
  return {
    name: d.rdName ?? sel?.name ?? '',
    label: d.rdLabel ?? '',
    placeholder: d.rdPlaceholder ?? '',
    required: d.rdRequired === 'true',
    disabled: sel?.disabled ?? d.rdDisabled === 'true',
    floating: d.rdFloating === 'true',
    size: (d.rdSize ?? 'medium') as 'medium' | 'large',
    clearable: d.rdClear === 'true',
    searchable: d.rdSearchable === 'true',
    helpText: d.rdHelp || undefined,
  }
}

function optionsFromSelect(sel: HTMLSelectElement, allowEmpty = false, emptyLabel = '---------'): SelectOption[] {
  // Сохраняем текст empty-option (кастомный лейбл вместо дефолтного "---------").
  let nativeEmpty: string | undefined
  const opts: SelectOption[] = []
  for (const o of Array.from(sel.options)) {
    if (o.value === '') nativeEmpty = o.text
    else opts.push({ value: o.value, label: o.text })
  }
  if (allowEmpty) {
    opts.unshift({ value: '', label: nativeEmpty ?? emptyLabel })
  }
  return opts
}

function buildProps(el: HTMLElement): SelectFieldProps {
  const sel = el.querySelector<HTMLSelectElement>('select')
  const base = readBase(el, sel)

  if (el.dataset.rdLookup != null) {
    return {
      ...base,
      mode: 'lookup',
      lookupUrl: el.dataset.rdLookupUrl ?? '',
      pageSize: Number(el.dataset.rdPageSize ?? 100),
      idField: el.dataset.rdIdField || undefined,
      initialValue: el.dataset.rdInitialValue
        ? { value: el.dataset.rdInitialValue, label: el.dataset.rdInitialLabel ?? '' }
        : null,
    }
  }

  if (el.dataset.rdLookupChips != null) {
    const initialValues: SelectOption[] = el.dataset.rdInitialValues
      ? JSON.parse(el.dataset.rdInitialValues)
      : []
    return {
      ...base,
      mode: 'lookup-chips',
      lookupUrl: el.dataset.rdLookupUrl ?? '',
      pageSize: Number(el.dataset.rdPageSize ?? 100),
      idField: el.dataset.rdIdField || undefined,
      initialValues,
    }
  }

  if (sel?.multiple) {
    const opts = optionsFromSelect(sel)
    const initialValues = opts.filter(o => {
      const opt = sel.querySelector<HTMLOptionElement>(`option[value="${CSS.escape(o.value)}"]`)
      return opt?.selected ?? false
    })
    return {
      ...base,
      name: sel.name,
      mode: 'static-chips',
      options: opts,
      initialValues: initialValues.map(o => o.value),
    }
  }

  const allowEmpty = el.dataset.rdAllowEmpty === 'true'
  const emptyLabel = el.dataset.rdEmptyLabel || '---------'
  return {
    ...base,
    name: sel?.name ?? base.name,
    mode: 'static',
    options: sel ? optionsFromSelect(sel, allowEmpty, emptyLabel) : [],
    initialValue: sel?.value ?? '',
  }
}

function selectMounted(): void {
  window.__rdSelectsPending = (window.__rdSelectsPending ?? 1) - 1
  if (window.__rdSelectsPending === 0) {
    document.dispatchEvent(new Event('rd:fields-ready'))
  }
}

function mount(el: HTMLElement): void {
  const props = buildProps(el)
  const root = document.createElement('div')
  root.style.display = 'contents'
  el.replaceWith(root)

  // Register setter for Django popup callbacks (dismissAdd/Change/Delete).
  // Django derives the popup window name as "id_<fieldname>__N", so we register
  // under both the plain name and the "id_"-prefixed name to match what
  // stripPopupIndex() returns in related_widget_logic.js.
  if (props.name) {
    window.__rdSetters = window.__rdSetters ?? {}
    const setterRef: SetterRef = { current: null }
    const setter = (v: string, l?: string) => setterRef.current?.(v, l)
    window.__rdSetters[props.name] = setter
    window.__rdSetters['id_' + props.name] = setter
    ;(props as SelectFieldProps & { setterRef: SetterRef }).setterRef = setterRef
  }

  // Inject onChange to update related widget button hrefs
  const modeProps = props as { onChange?: (v: string) => void }
  const origOnChange = modeProps.onChange
  modeProps.onChange = (value: string) => {
    origOnChange?.(value)
    window.RDRelatedLinks?.updateLinks({ value, closest: sel => root.closest(sel) })
    root.dispatchEvent(new CustomEvent('rd:change', { detail: { name: props.name, value }, bubbles: true }))
  }

  window.__rdSelectsPending = (window.__rdSelectsPending ?? 0) + 1
  const reactRoot = ReactDOM.createRoot(root)
  // Stash root reference for external code that needs to unmount this
  // widget — e.g. dependent_tags.js rebuilds the tags <select> when the
  // address book changes and must unmount the old React tree first.
  ;(root as unknown as { __rdReactRoot: ReturnType<typeof ReactDOM.createRoot> }).__rdReactRoot = reactRoot
  reactRoot.render(
    React.createElement(SelectField, { ...props, __onMounted: selectMounted }),
  )

  // Initial update of related button hrefs (delay to let related_widget_logic.js init first)
  if (root.closest('.related-widget-wrapper')) {
    let initVal = ''
    if ('initialValue' in props && props.initialValue) {
      const iv = props.initialValue
      initVal = typeof iv === 'string' ? iv : (iv as SelectOption).value
    }
    setTimeout(() => {
      window.RDRelatedLinks?.updateLinks({ value: initVal, closest: sel => root.closest(sel) })
    }, 150)
  }
}

const SKIP_INIT = '[data-rd-init], .empty-form'

function wrapAndMount(sel: HTMLElement): void {
  if (sel.closest(SKIP_INIT)) return
  const wrapper = document.createElement('div')
  // Copy data-rd-* attrs so readBase() can find them on the wrapper
  copyDataAttrs(sel, wrapper)
  sel.replaceWith(wrapper)
  wrapper.appendChild(sel)
  mount(wrapper)
}

function isPrefixTemplate(el: HTMLElement): boolean {
  const name =
    (el as HTMLSelectElement).name ||
    el.querySelector<HTMLSelectElement>('select')?.name ||
    el.dataset.rdName ||
    ''
  return name.includes('__prefix__')
}

export function initSelectFields(root: ParentNode = document): void {
  root
    .querySelectorAll<HTMLElement>('select[data-rd-styled]:not([multiple])')
    .forEach(el => {
      if (isPrefixTemplate(el)) return
      wrapAndMount(el)
    })

  // :not([data-m2m]) — исключаем скрытые select'ы M2M виджета (RDM2MWidget тоже ставит data-rd-styled)
  root
    .querySelectorAll<HTMLElement>('select[multiple][data-rd-styled]:not([data-m2m])')
    .forEach(el => {
      if (isPrefixTemplate(el)) return
      wrapAndMount(el)
    })

  root
    .querySelectorAll<HTMLElement>('[data-rd-lookup],[data-rd-lookup-chips]')
    .forEach(el => {
      if (el.closest(SKIP_INIT)) return
      if (isPrefixTemplate(el)) return
      mount(el)
    })
}

// Expose for vanilla JS files that need to re-mount widgets after
// mutating the DOM (e.g. dependent_tags.js after rebuilding the tags
// <select> in response to an address-book change).
;(window as unknown as { rdInitSelectFields?: () => void }).rdInitSelectFields = initSelectFields
